import styled from 'styled-components';
import { colors, fontSize, fontWeight, spacing, buttonDefaults } from '../../style/tokens';

/**
 * Form furniture for the GLP-1 journey screens, mirroring DemographicsForm's
 * look so the two read as one flow.
 *
 * Deliberately NOT imported from DemographicsForm: those styles are private to a
 * component every partner renders, including CE. Duplicating a few rules is
 * cheaper than coupling the shared demographics screen to an optional feature.
 */

export const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 14px;
`;

export const Field = styled.div`
  background: #fff;
  border: 1.5px solid ${p => p.theme.coloreBottoneInattivo || p.theme.gradient1};
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const FieldInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

export const FieldLabel = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.theme.coloreBottoneInattivo || p.theme.gradient1};
  margin-bottom: ${spacing.sm};
`;

export const FieldInput = styled.input`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  font-family: 'Outfit', sans-serif;
  color: ${colors.text};
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  padding: 0;
  &::placeholder { color: #ccc; font-weight: ${fontWeight.normal}; }
`;

export const FieldUnit = styled.span`
  font-size: 14px;
  font-weight: ${fontWeight.semibold};
  color: ${colors.muted};
  flex-shrink: 0;
`;

export const ToggleGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const ToggleBtn = styled.button<{ active: boolean }>`
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: 50px;
  border: 1.5px solid ${({ active, theme }) => (active ? (theme.coloreBottoneInattivo || buttonDefaults.inactive) : '#ddd')};
  background: ${({ active, theme }) => (active ? (theme.coloreBottoneInattivo || buttonDefaults.inactive) : '#fff')};
  color: ${({ active }) => (active ? '#fff' : colors.muted)};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
`;

export const FormActions = styled.div`
  padding: 20px 14px 8px;
`;

export const BtnPrimary = styled.button`
  display: block;
  width: 100%;
  padding: 18px 0;
  border-radius: 50px;
  border: none;
  background: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  color: #fff;
  font-size: 17px;
  font-weight: ${fontWeight.bold};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; } }
  &:active { transform: scale(0.98); }
`;

/**
 * Skip is styled as a peer of the primary action, not a faint afterthought:
 * §7 calls the step "fully skippable", which a user has to be able to see.
 */
export const BtnSkip = styled.button`
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 14px 0;
  border-radius: 50px;
  border: 1.5px solid #ddd;
  background: transparent;
  color: ${colors.muted};
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
`;

export const HelpText = styled.p`
  font-size: ${fontSize.sm};
  color: ${colors.muted};
  line-height: 1.55;
  padding: 0 16px 4px;
`;
